import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { CalendarIcon, Printer } from 'lucide-react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'
import { supabase } from '@/integrations/supabase/client'
import { api } from '@/lib/supabase'
import { CellularProductInventoryReport } from './CellularProductInventoryReport'

interface Unit {
  id: string
  unit_identifier: string
  unit_status: string
  collection_date: string | null
  volume_ml: number | null
  total_nucleated_cells: number | null
  cd34_positive_cells: number | null
  viability_percentage: number | null
  selected_for_infusion: boolean
  unit_requested_date: string | null
  notes: string | null
  sterility?: string | null
}

interface EditableUnit {
  id: string
  unit_identifier: string
  collection_date: string
  volume_ml: string
  total_nucleated_cells: string
  cd34_positive_cells: string
  viability_percentage: string
  sterility: string
}

interface InfusionFormProps {
  orderId: string
  orderStatus: string
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  onOrderUpdate?: () => void
}

export function InfusionForm({ orderId, orderStatus, isOpen, onClose, onSubmit, onOrderUpdate }: InfusionFormProps) {
  const { user } = useAuth()
  const [units, setUnits] = useState<Unit[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [selectedUnits, setSelectedUnits] = useState<Record<string, boolean>>({})
  const [requestedDates, setRequestedDates] = useState<Record<string, Date | undefined>>({})
  const [editableUnits, setEditableUnits] = useState<Record<string, EditableUnit>>({})
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({})
  const [dateValidationErrors, setDateValidationErrors] = useState<Record<string, boolean>>({})
  const [newRows, setNewRows] = useState<Record<string, EditableUnit>>({}) // New empty rows for supervisors
  const [showPrintReport, setShowPrintReport] = useState(false)
  const [patientInfo, setPatientInfo] = useState<any>(null)
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false) // Track if save has been attempted

  const isPhysician = user?.role === 'physician'
  const isClinician = user?.role === 'clinician'
  const isSupervisor = user?.role === 'supervisor'
  
  // Access control logic updated to include clinicians
  const canEnterValues = isSupervisor // Supervisors can enter values at any status
  const canPlaceOrder = isPhysician && (
    orderStatus === 'results_final' || 
    orderStatus === 'infusion_ordered' || 
    orderStatus === 'infusion_shipped' || 
    orderStatus === 'infusion_completed'
  ) // Only physicians can place orders from results_final onwards
  const canOnlyView = !canEnterValues && !canPlaceOrder

  // Convert UUID to numerical format for display
  const getOrderNumber = (id: string) => {
    if (!id) return 'N/A'
    // Take first 8 characters of UUID and convert to decimal
    const hex = id.replace(/-/g, '').substring(0, 8)
    return parseInt(hex, 16).toString().substring(0, 6)
  }

  useEffect(() => {
    if (isOpen && orderId) {
      fetchUnits()
    } else if (!isOpen) {
      // Clear state when popup closes
      setNewRows({})
      setEditableUnits({})
      setValidationErrors({})
      setSelectedUnits({})
      setRequestedDates({})
      setDateValidationErrors({})
      setHasAttemptedSave(false)
    }
  }, [isOpen, orderId])

  // Clear state when orderId changes
  useEffect(() => {
    setNewRows({})
    setEditableUnits({})
    setValidationErrors({})
    setSelectedUnits({})
    setRequestedDates({})
    setDateValidationErrors({})
    setHasAttemptedSave(false)
  }, [orderId])

  const fetchUnits = async () => {
    try {
      setLoading(true)
      
      // Fetch units and patient information
      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select('*')
        .eq('order_id', orderId)
        .order('unit_identifier')

      if (unitsError) {
        console.error('Supabase error:', unitsError)
        throw unitsError
      }

      // Fetch patient information from orders table
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          patients (
            first_name,
            last_name,
            gender,
            date_of_birth,
            mrn,
            abo_rh,
            physician
          )
        `)
        .eq('id', orderId)
        .single()

      if (orderError) {
        console.error('Order fetch error:', orderError)
      }

      setUnits(unitsData || [])
      setPatientInfo(orderData?.patients || null)
      
      // Initialize selected units and dates from existing data
      const initialSelected: Record<string, boolean> = {}
      const initialDates: Record<string, Date | undefined> = {}
      const initialEditable: Record<string, EditableUnit> = {}
      
      unitsData?.forEach(unit => {
        initialSelected[unit.id] = unit.selected_for_infusion || false
        if (unit.unit_requested_date) {
          initialDates[unit.id] = new Date(unit.unit_requested_date)
        }
        
        // Initialize editable form data
        initialEditable[unit.id] = {
          id: unit.id,
          unit_identifier: unit.unit_identifier || '',
          collection_date: unit.collection_date || '',
          volume_ml: unit.volume_ml?.toString() || '',
          total_nucleated_cells: unit.total_nucleated_cells?.toString() || '',
          cd34_positive_cells: unit.cd34_positive_cells?.toString() || '',
          viability_percentage: unit.viability_percentage?.toString() || '',
          sterility: (unit as any).sterility || 'No Growth'
        }
      })
      
      setSelectedUnits(initialSelected)
      setRequestedDates(initialDates)
      setEditableUnits(initialEditable)
    } catch (error) {
      toast.error('Failed to load units')
      console.error('Error fetching units:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUnitSelection = (unitId: string, selected: boolean) => {
    if (!canPlaceOrder) return
    
    setSelectedUnits(prev => ({
      ...prev,
      [unitId]: selected
    }))
  }

  const handleDateChange = (unitId: string, date: Date | undefined) => {
    if (!canPlaceOrder) return
    
    setRequestedDates(prev => ({
      ...prev,
      [unitId]: date
    }))
  }

  const handleUnitDataChange = (unitId: string, field: keyof EditableUnit, value: string) => {
    if (!canEnterValues) return
    
    // Check if it's a new row
    const isNewRow = newRows[unitId]
    
    if (isNewRow) {
      setNewRows(prev => ({
        ...prev,
        [unitId]: {
          ...prev[unitId],
          [field]: value
        }
      }))
    } else {
      setEditableUnits(prev => ({
        ...prev,
        [unitId]: {
          ...prev[unitId],
          [field]: value
        }
      }))
    }
    
    // Don't clear errors until save is attempted - let user finish typing
  }

  // Helper function to check if a field has validation error
  const hasFieldError = (unitId: string, fieldName: string) => {
    if (!hasAttemptedSave) return false
    return validationErrors[unitId]?.some(error => error.includes(fieldName)) || false
  }

  // Helper function to get field error message
  const getFieldError = (unitId: string, fieldName: string) => {
    if (!hasAttemptedSave) return null
    return validationErrors[unitId]?.find(error => error.includes(fieldName)) || null
  }
  // Check if a row is complete (all required fields filled)
  const isRowComplete = (rowData: EditableUnit): boolean => {
    return !!(
      rowData.unit_identifier?.trim() &&
      rowData.collection_date?.trim() &&
      rowData.volume_ml?.trim() &&
      rowData.total_nucleated_cells?.trim() &&
      rowData.cd34_positive_cells?.trim() &&
      rowData.viability_percentage?.trim()
    )
  }

  // Check if there are any incomplete empty rows
  const hasIncompleteRows = (): boolean => {
    return Object.values(newRows).some(row => !isRowComplete(row))
  }

  // Add new empty row for supervisor data entry
  const addNewRow = () => {
    if (!canEnterValues) return
    
    // Prevent adding new rows if there are already incomplete rows
    if (hasIncompleteRows()) {
      toast.error('Please complete the current empty row before adding another')
      return
    }
    
    const newId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newRow: EditableUnit = {
      id: newId,
      unit_identifier: '',
      collection_date: '',
      volume_ml: '',
      total_nucleated_cells: '',
      cd34_positive_cells: '',
      viability_percentage: '',
      sterility: 'No Growth'
    }
    
    setNewRows(prev => ({
      ...prev,
      [newId]: newRow
    }))
  }

  // Save new row data to database
  const saveNewRowData = async (rowId: string) => {
    const rowData = newRows[rowId]
    if (!rowData) return
    
    try {
      setSubmitting(true)
      
      // Validate row data
      const errors: string[] = []
      if (!rowData.unit_identifier) errors.push('Unit Number is required')
      if (!rowData.collection_date) errors.push('Collection Date is required')
      if (!rowData.volume_ml) errors.push('Volume is required')
      if (!rowData.total_nucleated_cells) errors.push('TNC is required')
      if (!rowData.cd34_positive_cells) errors.push('CD34 is required')
      if (!rowData.viability_percentage) errors.push('Viability is required')
      
      if (errors.length > 0) {
        setValidationErrors(prev => ({ ...prev, [rowId]: errors }))
        toast.error('Please fill all required fields')
        return
      }
      
      // Insert new unit to database
      const { data, error } = await supabase
        .from('units')
        .insert({
          order_id: orderId,
          unit_identifier: rowData.unit_identifier,
          collection_date: rowData.collection_date,
          volume_ml: parseFloat(rowData.volume_ml),
          total_nucleated_cells: parseFloat(rowData.total_nucleated_cells),
          cd34_positive_cells: parseFloat(rowData.cd34_positive_cells),
          viability_percentage: parseFloat(rowData.viability_percentage),
          sterility: rowData.sterility,
          unit_status: 'collected'
        })
        .select()
        .single()
      
      if (error) throw error
      
      // Remove from new rows and add to existing units
      setNewRows(prev => {
        const { [rowId]: removed, ...rest } = prev
        return rest
      })
      
      // Refresh units data
      fetchUnits()
      
      toast.success('Unit data saved successfully')
      
    } catch (error) {
      console.error('Error saving new unit:', error)
      toast.error('Failed to save unit data')
    } finally {
      setSubmitting(false)
    }
  }

  const validateUnit = (unit: EditableUnit): string[] => {
    const errors: string[] = []
    
    if (!unit.unit_identifier.trim()) errors.push('Unit Number is required')
    if (!unit.collection_date.trim()) errors.push('Collection Date is required')
    if (!unit.volume_ml.trim()) errors.push('Volume is required')
    if (!unit.total_nucleated_cells.trim()) errors.push('TNC is required')
    if (!unit.cd34_positive_cells.trim()) errors.push('Total CD34 is required')
    if (!unit.viability_percentage.trim()) errors.push('Viability is required')
    if (!unit.sterility.trim()) errors.push('Sterility is required')
    
    // Validate numeric fields
    if (unit.volume_ml && isNaN(Number(unit.volume_ml))) errors.push('Volume must be a number')
    if (unit.total_nucleated_cells && isNaN(Number(unit.total_nucleated_cells))) errors.push('TNC must be a number')
    if (unit.cd34_positive_cells && isNaN(Number(unit.cd34_positive_cells))) errors.push('CD34 must be a number')
    if (unit.viability_percentage && (isNaN(Number(unit.viability_percentage)) || Number(unit.viability_percentage) < 0 || Number(unit.viability_percentage) > 100)) {
      errors.push('Viability must be between 0-100')
    }
    
    return errors
  }

  const saveUnitData = async () => {
    if (!canEnterValues) return
    
    try {
      setSubmitting(true)
      setHasAttemptedSave(true) // Mark that save has been attempted
      console.log('Saving unit data...', { editableUnits, newRows })
      
      const errors: Record<string, string[]> = {}
      let hasErrors = false
      
      // Validate all existing units
      Object.values(editableUnits).forEach(unit => {
        const unitErrors = validateUnit(unit)
        if (unitErrors.length > 0) {
          errors[unit.id] = unitErrors
          hasErrors = true
        }
      })
      
      // Validate all new rows
      Object.values(newRows).forEach(unit => {
        const unitErrors = validateUnit(unit)
        if (unitErrors.length > 0) {
          errors[unit.id] = unitErrors
          hasErrors = true
        }
      })
      
      if (hasErrors) {
        setValidationErrors(errors)
        toast.error('Please fix validation errors before saving')
        return
      }
      
      // Update existing units
      if (Object.keys(editableUnits).length > 0) {
        console.log('Updating existing units:', Object.keys(editableUnits))
        const updates = Object.values(editableUnits).map(unit => ({
          id: unit.id,
          unit_identifier: unit.unit_identifier,
          collection_date: unit.collection_date,
          volume_ml: Number(unit.volume_ml),
          total_nucleated_cells: Number(unit.total_nucleated_cells),
          cd34_positive_cells: Number(unit.cd34_positive_cells),
          viability_percentage: Number(unit.viability_percentage),
          sterility: unit.sterility
        }))
        
        for (const update of updates) {
          const { error } = await supabase
            .from('units')
            .update({
              unit_identifier: update.unit_identifier,
              collection_date: update.collection_date,
              volume_ml: update.volume_ml,
              total_nucleated_cells: update.total_nucleated_cells,
              cd34_positive_cells: update.cd34_positive_cells,
              viability_percentage: update.viability_percentage,
              sterility: update.sterility
            })
            .eq('id', update.id)
          
          if (error) {
            console.error('Error updating unit:', update.id, error)
            throw error
          }
        }
      }
      
      // Insert new rows
      if (Object.keys(newRows).length > 0) {
        console.log('Inserting new units:', Object.keys(newRows))
        const newUnits = Object.values(newRows).map(unit => ({
          order_id: orderId,
          unit_identifier: unit.unit_identifier,
          collection_date: unit.collection_date,
          volume_ml: Number(unit.volume_ml),
          total_nucleated_cells: Number(unit.total_nucleated_cells),
          cd34_positive_cells: Number(unit.cd34_positive_cells),
          viability_percentage: Number(unit.viability_percentage),
          sterility: unit.sterility,
          unit_status: 'collected' as const // Default status for new units
        }))
        
        const { error } = await supabase
          .from('units')
          .insert(newUnits)
        
        if (error) {
          console.error('Error inserting new units:', error)
          throw error
        }
        
        // Clear new rows after successful insert
        setNewRows({})
      }
      
      toast.success('Unit data saved successfully')
      await fetchUnits() // Refresh data
      setValidationErrors({})
      console.log('Save completed successfully')
    } catch (error) {
      toast.error('Failed to save unit data')
      console.error('Error saving unit data:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const saveAndCloseUnitData = async () => {
    await saveUnitData()
    // Only close if save was successful (no validation errors)
    if (Object.keys(validationErrors).length === 0) {
      onClose()
    }
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      
      // Validate that selected units have requested dates
      const selectedUnitIds = Object.keys(selectedUnits).filter(unitId => selectedUnits[unitId])
      const missingDates = selectedUnitIds.filter(unitId => !requestedDates[unitId])
      
      if (missingDates.length > 0) {
        // Set inline validation errors instead of toast
        const dateErrors: Record<string, boolean> = {}
        missingDates.forEach(unitId => {
          dateErrors[unitId] = true
        })
        setDateValidationErrors(dateErrors)
        setSubmitting(false)
        return
      }
      
      // Clear any existing date validation errors
      setDateValidationErrors({})
      
      // Only update selected units to avoid changing status of already shipped/infused units
      const selectedUnitUpdates = units
        .filter(unit => selectedUnits[unit.id])
        .map(unit => ({
          id: unit.id,
          selected_for_infusion: true,
          unit_requested_date: requestedDates[unit.id] 
            ? format(requestedDates[unit.id]!, 'yyyy-MM-dd')
            : null,
          unit_status: 'ordered' as any
        }))

      for (const update of selectedUnitUpdates) {
        const { error } = await supabase
          .from('units')
          .update({
            selected_for_infusion: update.selected_for_infusion,
            unit_requested_date: update.unit_requested_date,
            unit_status: update.unit_status
          })
          .eq('id', update.id)

        if (error) throw error
      }

      // Send notification email to lab
      const selectedCount = Object.values(selectedUnits).filter(Boolean).length
      if (selectedCount > 0) {
        await sendLabNotification(selectedCount)
      }

      toast.success(`Infusion order submitted for ${selectedCount} unit(s)`)
      onSubmit()
      onClose()
    } catch (error) {
      toast.error('Failed to submit infusion order')
      console.error('Error submitting infusion order:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const sendLabNotification = async (unitCount: number) => {
    try {
      // This would typically call an edge function to send email
      // For now, we'll log the action
      // Sending lab notification for units
      
      // Log email action in database
      const { error } = await supabase
        .from('email_log')
        .insert({
          order_id: orderId,
          recipient_email: 'lab@pragmatrix.com',
          subject: `Infusion Order Request - ${unitCount} Unit(s)`,
          body: `Physician has requested ${unitCount} unit(s) for infusion. Please prepare units for shipment.`,
          status: 'sent'
        })

      if (error) {
        console.error('Failed to log email:', error)
      }
    } catch (error) {
      console.error('Error sending lab notification:', error)
    }
  }

  const handleStatusChange = async (unitId: string, newStatus: 'ordered' | 'shipped' | 'infused' | 'available') => {
    // Allow supervisors to change any status, clinicians only from shipped to infused
    if (!isSupervisor && !(isClinician && newStatus === 'infused')) return
    
    try {
      const { error } = await supabase
        .from('units')
        .update({ unit_status: newStatus })
        .eq('id', unitId)
      
      if (error) throw error
      
      // If unit status changed to 'shipped', update order status to 'infusion_shipped'
      if (newStatus === 'shipped') {
        if (orderStatus !== 'infusion_shipped') {
          await api.updateOrderStatus(orderId, 'infusion_shipped', user?.id, orderStatus)
          // Trigger parent refresh to update the orders grid
          if (onOrderUpdate) {
            onOrderUpdate()
          }
        }
      }
      
      // Refresh data
      await fetchUnits()
      toast.success(`Unit status updated to ${newStatus}`)
    } catch (error) {
      console.error('Error updating unit status:', error)
      toast.error('Failed to update unit status')
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'ordered': return 'bg-blue-100 text-blue-800'
      case 'shipped': return 'bg-yellow-100 text-yellow-800' 
      case 'infused': return 'bg-green-100 text-green-800'
      case 'available': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] w-[90vw] max-h-[90vh] overflow-x-hidden overflow-y-auto flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <img 
                src="/lovable-uploads/5a6285bd-aaa9-4c5f-8b8f-a7c3b2e2f4d4.png" 
                alt="PragLife - Pragmatrix Lifesciences"
                className="h-12 w-auto"
              />
              <DialogTitle className="text-xl font-semibold">
                Cellular Product Inventory Report and Return Request Form
                {/* Show patient info when available */}
                {(patientInfo?.first_name && patientInfo?.last_name && patientInfo?.mrn) && (
                  <span className="text-lg font-normal text-gray-600 ml-3">
                    for {patientInfo.first_name} {patientInfo.last_name} (MRN: {patientInfo.mrn})
                  </span>
                )}
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Form for managing cellular product inventory and infusion orders
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pb-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-medical-blue"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Units Table */}
              <div className="overflow-x-hidden">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Unit Number *</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Collection<br/>Date *</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Vol(ml) *</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">TNC<br/>(x10⁶/kg) *</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Total CD34<br/>(x10⁶/kg) *</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Via(%) *</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Sterility *</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Ordered<br/>Date</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Unit Requested<br/>For</th>
                    <th className="border border-gray-300 p-2 text-left text-xs font-medium">Infusion<br/>Order</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Render existing units */}
                  {units.length === 0 && Object.keys(newRows).length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-gray-500 border border-gray-300">
                        No units found for this order. Order ID: {getOrderNumber(orderId)}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {units.map((unit) => {
                        return (
                          <tr key={unit.id} className="hover:bg-gray-50">
                            {/* Unit Number */}
                            <td className="border border-gray-300 p-2 text-xs">
                              {canEnterValues ? (
                                <div>
                                   <Input
                                     value={editableUnits[unit.id]?.unit_identifier || ''}
                                     onChange={(e) => handleUnitDataChange(unit.id, 'unit_identifier', e.target.value)}
                                     className={cn("w-32", hasFieldError(unit.id, 'Unit Number') && "border-red-500")}
                                     placeholder="Unit Number"
                                   />
                                   {hasFieldError(unit.id, 'Unit Number') && (
                                     <div className="text-xs text-red-500 mt-1">{getFieldError(unit.id, 'Unit Number')}</div>
                                   )}
                                </div>
                              ) : (
                                unit.unit_identifier
                              )}
                            </td>
                            
                            {/* Collection Date */}
                            <td className="border border-gray-300 p-2 text-xs">
                              {canEnterValues ? (
                                <div>
                                   <Input
                                     type="date"
                                     value={editableUnits[unit.id]?.collection_date || ''}
                                     onChange={(e) => handleUnitDataChange(unit.id, 'collection_date', e.target.value)}
                                     className={cn("w-26", hasFieldError(unit.id, 'Collection Date') && "border-red-500")}
                                   />
                                   {hasFieldError(unit.id, 'Collection Date') && (
                                     <div className="text-xs text-red-500 mt-1">{getFieldError(unit.id, 'Collection Date')}</div>
                                   )}
                                </div>
                              ) : (
                                unit.collection_date ? format(new Date(unit.collection_date), 'dd-MMM-yy') : '-'
                              )}
                            </td>
                            
                            {/* Volume */}
                            <td className="border border-gray-300 p-2 text-xs">
                              {canEnterValues ? (
                                <div>
                                   <Input
                                     type="number"
                                     value={editableUnits[unit.id]?.volume_ml || ''}
                                     onChange={(e) => handleUnitDataChange(unit.id, 'volume_ml', e.target.value)}
                                     className={cn("w-20", hasFieldError(unit.id, 'Volume') && "border-red-500")}
                                     placeholder="0.0"
                                     step="0.1"
                                   />
                                   {hasFieldError(unit.id, 'Volume') && (
                                     <div className="text-xs text-red-500 mt-1">{getFieldError(unit.id, 'Volume')}</div>
                                   )}
                                </div>
                              ) : (
                                unit.volume_ml || '-'
                              )}
                            </td>
                            
                            {/* TNC */}
                            <td className="border border-gray-300 p-2 text-xs">
                              {canEnterValues ? (
                                <div>
                                   <Input
                                     type="number"
                                     value={editableUnits[unit.id]?.total_nucleated_cells || ''}
                                     onChange={(e) => handleUnitDataChange(unit.id, 'total_nucleated_cells', e.target.value)}
                                     className={cn("w-24", hasFieldError(unit.id, 'TNC') && "border-red-500")}
                                     placeholder="0.0"
                                     step="0.1"
                                   />
                                   {hasFieldError(unit.id, 'TNC') && (
                                     <div className="text-xs text-red-500 mt-1">{getFieldError(unit.id, 'TNC')}</div>
                                   )}
                                </div>
                              ) : (
                                unit.total_nucleated_cells ? (unit.total_nucleated_cells / 1000000).toFixed(1) : '-'
                              )}
                            </td>
                            
                            {/* CD34 */}
                            <td className="border border-gray-300 p-3 text-sm">
                              {canEnterValues ? (
                                <div>
                                   <Input
                                     type="number"
                                     value={editableUnits[unit.id]?.cd34_positive_cells || ''}
                                     onChange={(e) => handleUnitDataChange(unit.id, 'cd34_positive_cells', e.target.value)}
                                     className={cn("w-28", hasFieldError(unit.id, 'CD34') && "border-red-500")}
                                     placeholder="0.0"
                                     step="0.1"
                                   />
                                   {hasFieldError(unit.id, 'CD34') && (
                                     <div className="text-xs text-red-500 mt-1">{getFieldError(unit.id, 'CD34')}</div>
                                   )}
                                </div>
                              ) : (
                                unit.cd34_positive_cells ? (unit.cd34_positive_cells / 1000000).toFixed(1) : '-'
                              )}
                            </td>
                            
                            {/* Viability */}
                            <td className="border border-gray-300 p-2 text-xs">
                              {canEnterValues ? (
                                <div>
                                   <Input
                                     type="number"
                                     value={editableUnits[unit.id]?.viability_percentage || ''}
                                     onChange={(e) => handleUnitDataChange(unit.id, 'viability_percentage', e.target.value)}
                                     className={cn("w-16", hasFieldError(unit.id, 'Viability') && "border-red-500")}
                                     placeholder="0.0"
                                     step="0.1"
                                     max="100"
                                   />
                                   {hasFieldError(unit.id, 'Viability') && (
                                     <div className="text-xs text-red-500 mt-1">{getFieldError(unit.id, 'Viability')}</div>
                                   )}
                                </div>
                              ) : (
                                unit.viability_percentage ? `${unit.viability_percentage}%` : '-'
                              )}
                            </td>
                            
                            {/* Sterility */}
                            <td className="border border-gray-300 p-2 text-xs">
                              {canEnterValues ? (
                                <Select 
                                  value={editableUnits[unit.id]?.sterility || 'No Growth'} 
                                  onValueChange={(value) => handleUnitDataChange(unit.id, 'sterility', value)}
                                >
                                  <SelectTrigger className="w-24 bg-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white border shadow-lg z-50">
                                    <SelectItem value="No Growth">No Growth</SelectItem>
                                    <SelectItem value="Growth Detected">Growth Detected</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                (unit as any).sterility || 'No Growth'
                              )}
                            </td>
                            
            {/* Order Date */}
            <td className="border border-gray-300 p-2 text-xs">
              {unit.unit_requested_date ? new Date(unit.unit_requested_date).toLocaleDateString() : '-'}
            </td>
                            
                            {/* Unit Requested For */}
                            <td className="border border-gray-300 p-2 text-xs">
                              <div>
                                {canPlaceOrder ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        disabled={!selectedUnits[unit.id]}
                                        className={cn(
                                          "w-32 justify-start text-left font-normal",
                                          !requestedDates[unit.id] && "text-muted-foreground",
                                          dateValidationErrors[unit.id] && selectedUnits[unit.id] && "border-red-500"
                                        )}
                                      >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {requestedDates[unit.id] ? format(requestedDates[unit.id]!, "dd/MM/yyyy") : "Select date"}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <Calendar
                                        mode="single"
                                        selected={requestedDates[unit.id]}
                                        onSelect={(date) => {
                                          handleDateChange(unit.id, date)
                                          // Clear validation error when date is selected
                                          if (date) {
                                            setDateValidationErrors(prev => ({
                                              ...prev,
                                              [unit.id]: false
                                            }))
                                          }
                                        }}
                                        initialFocus
                                        className="pointer-events-auto"
                                      />
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <span className="text-sm text-muted-foreground">
                                    {requestedDates[unit.id] 
                                      ? format(requestedDates[unit.id]!, 'dd/MM/yyyy')
                                      : 'Not set'
                                    }
                                  </span>
                                )}
                                {dateValidationErrors[unit.id] && selectedUnits[unit.id] && (
                                  <div className="text-xs text-red-500 mt-1">
                                    Date required for selected unit
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="border border-gray-300 p-2 text-xs text-left">
                              {canPlaceOrder && (unit.unit_status === 'available' || unit.unit_status === 'collected') ? (
                                <Checkbox
                                  checked={selectedUnits[unit.id] || false}
                                  onCheckedChange={(checked) => handleUnitSelection(unit.id, !!checked)}
                                />
                              ) : isSupervisor && (unit.unit_status === 'ordered' || unit.unit_status === 'shipped') ? (
                                <Select
                                  value={unit.unit_status}
                                  onValueChange={(value) => handleStatusChange(unit.id, value as 'ordered' | 'shipped' | 'infused')}
                                >
                                  <SelectTrigger className="w-24 bg-white text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white border shadow-lg z-50">
                                    <SelectItem value="ordered">Ordered</SelectItem>
                                    <SelectItem value="shipped">Shipped</SelectItem>
                                    <SelectItem value="infused">Infused</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : isClinician && unit.unit_status === 'shipped' ? (
                                <Select
                                  value={unit.unit_status}
                                  onValueChange={(value) => handleStatusChange(unit.id, value as 'shipped' | 'infused')}
                                >
                                  <SelectTrigger className="w-24 bg-white text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white border shadow-lg z-50">
                                    <SelectItem value="shipped">Shipped</SelectItem>
                                    <SelectItem value="infused">Infused</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className={cn(
                                  "inline-block px-2 py-1 rounded-full text-xs font-medium",
                                  getStatusBadgeColor(unit.unit_status)
                                )}>
                                  {unit.unit_status === 'ordered' ? 'Ordered' : 
                                   unit.unit_status === 'shipped' ? 'Shipped' :
                                   unit.unit_status === 'infused' ? 'Infused' : 
                                   unit.unit_status === 'available' ? 'Available' : 
                                   'Available'}
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                      
                      {/* Render new empty rows for supervisor data entry */}
                      {Object.entries(newRows).map(([rowId, rowData]) => (
                         <tr key={rowId} className="hover:bg-blue-50 bg-blue-25">
                           {/* Unit Number */}
                           <td className="border border-gray-300 p-3 text-sm">
                             <div>
                               <Input
                                 value={rowData.unit_identifier}
                                 onChange={(e) => handleUnitDataChange(rowId, 'unit_identifier', e.target.value)}
                                 className={cn("w-32", hasFieldError(rowId, 'Unit Number') && "border-red-500")}
                                 placeholder="Unit Number"
                               />
                               {hasFieldError(rowId, 'Unit Number') && (
                                 <div className="text-xs text-red-500 mt-1">{getFieldError(rowId, 'Unit Number')}</div>
                               )}
                             </div>
                           </td>
                           
                           {/* Collection Date */}
                           <td className="border border-gray-300 p-3 text-sm">
                             <div>
                               <Input
                                 type="date"
                                 value={rowData.collection_date}
                                 onChange={(e) => handleUnitDataChange(rowId, 'collection_date', e.target.value)}
                                 className={cn("w-36", hasFieldError(rowId, 'Collection Date') && "border-red-500")}
                               />
                               {hasFieldError(rowId, 'Collection Date') && (
                                 <div className="text-xs text-red-500 mt-1">{getFieldError(rowId, 'Collection Date')}</div>
                               )}
                             </div>
                           </td>
                           
                           {/* Volume */}
                           <td className="border border-gray-300 p-3 text-sm">
                             <div>
                               <Input
                                 type="number"
                                 value={rowData.volume_ml}
                                 onChange={(e) => handleUnitDataChange(rowId, 'volume_ml', e.target.value)}
                                 className={cn("w-24", hasFieldError(rowId, 'Volume') && "border-red-500")}
                                 placeholder="0.0"
                                 step="0.1"
                               />
                               {hasFieldError(rowId, 'Volume') && (
                                 <div className="text-xs text-red-500 mt-1">{getFieldError(rowId, 'Volume')}</div>
                               )}
                             </div>
                           </td>
                           
                           {/* TNC */}
                           <td className="border border-gray-300 p-3 text-sm">
                             <div>
                               <Input
                                 type="number"
                                 value={rowData.total_nucleated_cells}
                                 onChange={(e) => handleUnitDataChange(rowId, 'total_nucleated_cells', e.target.value)}
                                 className={cn("w-28", hasFieldError(rowId, 'TNC') && "border-red-500")}
                                 placeholder="0.0"
                                 step="0.1"
                               />
                               {hasFieldError(rowId, 'TNC') && (
                                 <div className="text-xs text-red-500 mt-1">{getFieldError(rowId, 'TNC')}</div>
                               )}
                             </div>
                           </td>
                           
                           {/* CD34 */}
                           <td className="border border-gray-300 p-3 text-sm">
                             <div>
                               <Input
                                 type="number"
                                 value={rowData.cd34_positive_cells}
                                 onChange={(e) => handleUnitDataChange(rowId, 'cd34_positive_cells', e.target.value)}
                                 className={cn("w-28", hasFieldError(rowId, 'CD34') && "border-red-500")}
                                 placeholder="0.0"
                                 step="0.1"
                               />
                               {hasFieldError(rowId, 'CD34') && (
                                 <div className="text-xs text-red-500 mt-1">{getFieldError(rowId, 'CD34')}</div>
                               )}
                             </div>
                           </td>
                           
                           {/* Viability */}
                           <td className="border border-gray-300 p-3 text-sm">
                             <div>
                               <Input
                                 type="number"
                                 value={rowData.viability_percentage}
                                 onChange={(e) => handleUnitDataChange(rowId, 'viability_percentage', e.target.value)}
                                 className={cn("w-20", hasFieldError(rowId, 'Viability') && "border-red-500")}
                                 placeholder="0.0"
                                 step="0.1"
                                 max="100"
                               />
                               {hasFieldError(rowId, 'Viability') && (
                                 <div className="text-xs text-red-500 mt-1">{getFieldError(rowId, 'Viability')}</div>
                               )}
                             </div>
                           </td>
                          
                          {/* Sterility */}
                          <td className="border border-gray-300 p-3 text-sm">
                            <Select 
                              value={rowData.sterility} 
                              onValueChange={(value) => handleUnitDataChange(rowId, 'sterility', value)}
                            >
                              <SelectTrigger className="w-28 bg-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border shadow-lg z-50">
                                <SelectItem value="No Growth">No Growth</SelectItem>
                                <SelectItem value="Growth Detected">Growth Detected</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          
                          {/* Order Date */}
                          <td className="border border-gray-300 p-3 text-sm">-</td>
                          
                          {/* Unit Requested For */}
                          <td className="border border-gray-300 p-3 text-sm">-</td>
                          
                          {/* Action */}
                          <td className="border border-gray-300 p-3 text-center">
                            <Button
                              size="sm"
                              onClick={() => saveNewRowData(rowId)}
                              disabled={submitting}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                            >
                              Save
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>

        {/* Fixed Footer with Form Actions */}
        <div className="flex-shrink-0 border-t bg-gray-50 p-4 -mx-6 -mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowPrintReport(true)}
                variant="outline"
                size="sm"
                className="border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                <Printer className="h-4 w-4" />
              </Button>
              {canEnterValues && (
                <Button 
                  onClick={addNewRow}
                  variant="outline"
                  size="sm"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  disabled={submitting || hasIncompleteRows()}
                >
                  + Add Row
                </Button>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {canEnterValues && (
                <Button 
                  onClick={saveAndCloseUnitData} 
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {submitting ? 'Saving...' : 'Save & Close'}
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={onClose}
                className="border-medical-blue text-medical-blue hover:bg-medical-blue-light"
              >
                Cancel
              </Button>
              {canPlaceOrder && (
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="bg-medical-blue hover:bg-medical-blue-dark"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              )}
            </div>
          </div>
            
          {/* Validation Errors Summary */}
          {canEnterValues && Object.keys(validationErrors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mt-4">
              <h4 className="text-red-800 font-medium mb-2">Please fix the following errors:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {Object.entries(validationErrors).map(([unitId, errors]) => {
                  const unit = units.find(u => u.id === unitId)
                  return errors.map((error, index) => (
                    <li key={`${unitId}-${index}`}>
                      {unit?.unit_identifier || 'Unit'}: {error}
                    </li>
                  ))
                })}
              </ul>
            </div>
          )}
          
          {/* Access Control Information */}
          {canOnlyView && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                <strong>View Only:</strong> {isPhysician 
                  ? 'You can place orders from Results Final, Infusion Ordered, Infusion Shipped, and Infusion Completed statuses.' 
                  : isClinician
                  ? 'You have view-only access. Only physicians can place unit orders.'
                  : 'You have read-only access to this form.'
                }
              </div>
          )}
        </div>

        {/* Cellular Product Inventory Report */}
        <CellularProductInventoryReport
          isOpen={showPrintReport}
          onClose={() => setShowPrintReport(false)}
          units={units}
          orderId={orderId}
          patientInfo={patientInfo}
        />
      </DialogContent>
    </Dialog>
  )
}